'use client'

import { default as BoringAvatar } from "boring-avatars"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { formatImageURL } from "@/lib/util"

/**
 * One avatar for both shells - the public header and the signed-in user menu.
 *
 * `use client` is required rather than stylistic: boring-avatars calls useId and
 * ships no client directive of its own, so importing it straight into the
 * landing page (a Server Component) would run a hook on the server.
 *
 * An account with no picture gets a generated mark seeded on its email, the same
 * way the vendor panel does it. Seeding on something stable - not on initials or
 * a random value - is the point: one person is the same colours on every screen
 * and across sessions.
 */
export default function UserAvatar({ user, size = 32, className = '' }: {
  user: any
  size?: number
  className?: string
}) {
  // id backstops an account whose session carries no email.
  const seed = user?.email || String(user?.id ?? 'shop.mn')

  if (!user?.avatar) {
    return <BoringAvatar name={seed} size={size} variant="beam" className={className} />
  }

  const initials = [user?.lastName?.[0], user?.firstName?.[0]].filter(Boolean).join('').toUpperCase() || '·'

  return (
    // Sized inline so the picture and the generated mark are always the same
    // box; a className-only size would have to be kept in sync by hand.
    <Avatar className={className} style={{ width: size, height: size }}>
      <AvatarImage src={formatImageURL(user.avatar)} alt="" />
      {/* Only ever seen while the picture loads, or if its URL is dead. */}
      <AvatarFallback className={`font-semibold ${size < 28 ? 'text-[10px]' : 'text-xs'}`}>{initials}</AvatarFallback>
    </Avatar>
  )
}
